import { clsx } from 'clsx';
import { Button } from '@mui/material';
import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import { Modal, notification, Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDeleteMany, useListContext } from 'ra-core';
import { Close, RefreshTwoTone } from '@mui/icons-material';
import { useNotify, useRefresh, useResourceContext } from 'react-admin';

import { http } from '@network';
import { axiosErrorToString } from '@utils';
import { ConfirmDelete } from '@components';
import { ConfirmRemoval } from '../ConfirmRemoval';
import { ConfirmRestore } from '../ConfirmRestore';
import { useModalContext, useSettingsContext } from '@providers';
import cls from './GridActions.module.css';


interface Props {
  editComponent?: React.ReactElement;
  hideRemoveButton?: boolean;
  hasEditAccess: boolean;
  confirmRemoval?: boolean;
  confirmRestore?: boolean;
  summary?: React.ReactNode;
  mergeButton?: boolean;
  lockText?: string;
}

export const GridActions = ({
  editComponent, hideRemoveButton, hasEditAccess, confirmRemoval,
  confirmRestore, summary, mergeButton, lockText,
}: Props) => {
  const { selectedIds, onUnselectItems } = useListContext();
  const { isOpenedModal, showModal, hideModal, title } = useModalContext();
  const { setPanelName, panelName } = useSettingsContext();
  const resource = useResourceContext();
  const refresh = useRefresh();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  const [deleteMany, { isLoading }] = useDeleteMany(
    resource,
    { ids: selectedIds },
    {
      onSuccess: () => {
        refresh();
        notification.success({
          message: `Deleted items: ${selectedIds.length}`,
          placement: 'bottom',
        });
        onUnselectItems();
      },
      onError: (error: Error) => {
        notify(
          typeof error === 'string'
            ? error
            : error.message || 'ra.notification.http_error',
          {
            type: 'error',
            messageArgs: {
              _:
                typeof error === 'string'
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
        refresh();
      },
    },
  );

  if (!selectedIds || selectedIds.length === 0) {
    return null;
  }

  const defaultModalTitle = `Edit ${selectedIds.length} records`;
  const showDeleteBtn = !hideRemoveButton && hasEditAccess && !confirmRemoval;
  const showRemoveBtn = !hideRemoveButton && hasEditAccess && confirmRemoval;
  const showRestoreBtn = hasEditAccess && confirmRestore;

  const toggleRemoval = () => {
    setPanelName(panelName === 'ConfirmRemoval' ? null : 'ConfirmRemoval');
  };
  const toggleRestore = () => {
    setPanelName(panelName === 'ConfirmRestore' ? null : 'ConfirmRestore');
  };

  const onMergeClick = async () => {
    if (selectedIds.length < 2) {
      return notification.error({
        message: 'You need to select 2 or more records',
        placement: 'bottom',
      });
    }
    if (loading) return;
    setLoading(true);

    try {
      const url = `/${resource}/merge-bulk`;
      await http.post(url, { ids: selectedIds });
      refresh();
      notification.success({
        message: `Merged records: ${selectedIds.length}`,
        placement: 'bottom',
      });
      onUnselectItems();
    } catch (err) {
      const errorMessage = axiosErrorToString(err);
      notification.error({
        message: errorMessage,
        placement: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={cls.box}>
        <IconButton size="small" onClick={onUnselectItems}
          title="unselect" className={cls.unselect}>
          <Close fontSize="small" />
        </IconButton>
        <div className={cls.selectedText}>{selectedIds.length} selected records</div>

        {editComponent && hasEditAccess ? (
          <Tooltip placement="top" title={lockText} overlayClassName={cls.lockTooltip}>
            <Button
              variant="text"
              className={cls.editBtn}
              onClick={lockText ? undefined : showModal}>
              <svg xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none">
                <path d="M12 2.04973V2.49024C11.9039 2.92044 11.7286 3.31243 11.4375 3.64768C11.355 3.74263 11.2723 3.83782 11.183 3.9262C8.9041 6.18317 6.62384 8.43897 4.34217 10.6934C4.27138 10.7623 4.18607 10.8146 4.09232 10.8462C2.92816 11.2234 1.76307 11.5974 0.596571 11.9678C0.305707 12.0616 0.0621886 11.9462 0.00757848 11.678C-0.0111718 11.5866 0.00757848 11.4795 0.0342976 11.3873C0.367583 10.262 0.710947 9.1423 1.0379 8.01628C1.10166 7.79074 1.22377 7.58584 1.39228 7.4229C3.48997 5.33238 5.5839 3.23764 7.67362 1.13822C8.19323 0.614004 8.72176 0.140665 9.48419 0H9.95599C9.96419 0.0234442 9.97169 0.0185209 9.9806 0.0199276C10.717 0.140665 11.2251 0.592669 11.6428 1.17338C11.832 1.43619 11.9262 1.73933 12 2.04973Z"
                  fill="var(--primary-color)"
                />
              </svg>
              <span>Edit</span>
            </Button>
          </Tooltip>
        ) : null}

        {hasEditAccess && mergeButton ? (
          <Tooltip placement="top" title={lockText} overlayClassName={cls.lockTooltip}>
            <Button
              onClick={lockText ? undefined : onMergeClick}
              className={cls.mergeBtn}
              disabled={loading}>
              {loading ? <LoadingOutlined /> : (
                <svg xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none">
                  <rect width="28" height="28" rx="5" fill="none" />
                  <path fill="#247510"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M14.9925 10.0325V15.2025C14.9925 16.0025 14.6725 16.7625 14.1125 17.3225L9.7025 21.7425C9.3125 22.1325 8.6825 22.1325 8.2925 21.7425C7.9025 21.3525 7.9025 20.7225 8.2925 20.3325L12.7025 15.9225C12.8925 15.7325 12.9925 15.4825 12.9925 15.2125V10.0425H10.3625C10.1925 10.0425 10.0625 9.9125 10.0625 9.7425C10.0625 9.6625 10.0925 9.5825 10.1525 9.5225L13.6425 6.1425C13.8325 5.9525 14.1425 5.9525 14.3425 6.1425L17.8325 9.5125C17.9525 9.6325 17.9525 9.8225 17.8325 9.9325C17.7725 9.9925 17.7025 10.0225 17.6125 10.0225H14.9825L14.9925 10.0325ZM15.2825 18.7425C14.8925 18.3525 14.8925 17.7225 15.2825 17.3325C15.6725 16.9425 16.3025 16.9425 16.6925 17.3325L19.6925 20.3325C20.0825 20.7225 20.0825 21.3525 19.6925 21.7425C19.3025 22.1325 18.6725 22.1325 18.2825 21.7425L15.2825 18.7425Z" />
                </svg>
              )}
              <span>Merge</span>
            </Button>
          </Tooltip>
        ) : null}

        {showDeleteBtn && (
          <ConfirmDelete
            title="Are you sure?"
            cb={() => lockText ? undefined : void deleteMany()}
            overlayClassName={cls.overlayClassName}
            placement="bottom"
          >
            <Tooltip placement="top" title={lockText} overlayClassName={cls.lockTooltip}>
              <Button
                variant="text"
                className={cls.removeBtn}
                disabled={isLoading}>
                <DeleteIcon />
                <span>Delete</span>
              </Button>
            </Tooltip>
          </ConfirmDelete>
        )}

        {showRemoveBtn && (
          <Tooltip placement="top" title={lockText} overlayClassName={cls.lockTooltip}>
            <Button
              variant="text"
              className={cls.removeBtn}
              onClick={lockText ? undefined : toggleRemoval}>
              <DeleteIcon />
              <span>Delete</span>
            </Button>
            <ConfirmRemoval />
          </Tooltip>
        )}

        {showRestoreBtn && (
          <Tooltip placement="top" title={lockText} overlayClassName={cls.lockTooltip}>
            <Button
              variant="text"
              className={cls.removeBtn}
              onClick={lockText ? undefined : toggleRestore}>
              <RefreshTwoTone />
              <span>Restore</span>
            </Button>
            <ConfirmRestore />
          </Tooltip>
        )}
      </div>

      {summary && (
        <div className={clsx(cls.summary, 'list-summary')}>{summary}</div>
      )}

      <Modal open={isOpenedModal} onCancel={hideModal}
        closeIcon={<Close style={{ marginTop: 14 }} />}
        width={680} footer={false} className={cls.modal} title={false}
        destroyOnClose
      >
        <>
          <div className={cls.title}>
            {title || defaultModalTitle}
          </div>
          <div className={cls.modalContent}>
            {selectedIds.length > 0 && editComponent}
          </div>
        </>
      </Modal>
    </>
  );
};

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="15"
    viewBox="0 0 12 15"
    fill="none">
    <path d="M8.73038 1.90625C8.75639 2.01427 8.8664 2.10028 8.97842 2.10028H11.1087C11.4948 2.10028 11.8088 2.41432 11.8088 2.80037C11.8088 3.12042 11.5948 3.38845 11.3027 3.47246L10.9367 13.5258C10.9067 14.3439 10.2266 15 9.40647 15H2.80759C1.98948 15 1.30739 14.3419 1.27739 13.5258L0.911341 3.47246C0.619302 3.38845 0.405273 3.11842 0.405273 2.80037C0.405273 2.41432 0.719315 2.10028 1.10537 2.10028H3.23565C3.34567 2.10028 3.45568 2.01227 3.48368 1.90625L3.6337 1.30617C3.81973 0.566076 4.54383 0 5.30593 0H6.90814C7.67024 0 8.39634 0.566076 8.58036 1.30617L8.73038 1.90625ZM5.60597 5.30071V12.1016C5.60597 12.3777 5.83 12.6017 6.10603 12.6017C6.38207 12.6017 6.6061 12.3777 6.6061 12.1016V5.30071C6.6061 5.02467 6.38207 4.80064 6.10603 4.80064C5.83 4.80064 5.60597 5.02467 5.60597 5.30071ZM3.30566 5.31471L3.50569 12.1156C3.51369 12.3917 3.74372 12.6097 4.01976 12.6017C4.29579 12.5937 4.51382 12.3636 4.50582 12.0876L4.30579 5.28671C4.29779 5.01067 4.06776 4.79264 3.79172 4.80064C3.51569 4.80864 3.29766 5.03867 3.30566 5.31471ZM7.90627 5.2847L7.70625 12.0856C7.69825 12.3616 7.91627 12.5917 8.19231 12.5997C8.46835 12.6077 8.69838 12.3897 8.70638 12.1136L8.90641 5.31271C8.91441 5.03667 8.69638 4.80664 8.42034 4.79864C8.1443 4.79064 7.91427 5.00867 7.90627 5.2847ZM4.97588 2.09828H7.23418C7.28819 2.09828 7.3222 2.05427 7.31019 2.00227L7.22018 1.64422C7.19018 1.5262 7.02616 1.39819 6.90614 1.39819H5.30393C5.18391 1.39819 5.01989 1.5262 4.98988 1.64422L4.89987 2.00227C4.88587 2.05627 4.91988 2.09828 4.97588 2.09828Z"
      fill="#D32F2F" />
  </svg>
);
